import React, { ReactNode, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';

import { ReactComponent as InfoIcon } from '@/assets/icons/icon-info-fill.svg';
import { ReactComponent as SuccessIcon } from '@/assets/icons/flag-success.svg';
import { ReactComponent as WarningIcon } from '@/assets/icons/flag-warning.svg';
import { ReactComponent as DangerIcon } from '@/assets/icons/icon-flag.svg';
import { ReactComponent as QuestionMarkIcon } from '@/assets/icons/icon-help-fill.svg';
import { ReactComponent as FlagDangerIcon } from '@/assets/icons/flag-danger.svg';

/* --------------------------------------------------------------- */
/* inline alert styles were moved to the useGlobalStyles hook! */
/* --------------------------------------------------------------- */

interface InlineAlertAction {
	title: string;
	onClick(): void;
	disabled?: boolean;
}

interface InlineAlertProps {
	variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'attention' | 'flag-danger';
	title: string;
	description?: ReactNode;
	action?: InlineAlertAction | InlineAlertAction[];
	className?: string;
}

export const InlineAlert = ({ title, description, action, variant = 'info', className }: InlineAlertProps) => {
	const icon: Record<Exclude<typeof variant, undefined>, ReactNode> = useMemo(() => {
		return {
			primary: <InfoIcon width={24} height={24} />,
			success: <SuccessIcon width={24} height={24} />,
			warning: <WarningIcon width={24} height={24} />,
			danger: <DangerIcon width={24} height={24} />,
			info: <InfoIcon width={24} height={24} />,
			attention: <QuestionMarkIcon width={24} height={24} />,
			'flag-danger': <FlagDangerIcon width={24} height={24} />,
		};
	}, []);

	const actionsToRender = useMemo(() => {
		if (!action) {
			return [];
		} else if (Array.isArray(action)) {
			return action;
		} else {
			return [action];
		}
	}, [action]);

	return (
		<div
			className={classNames(
				'inline-alert',
				{
					[`inline-alert--${variant}`]: !!variant,
				},
				className
			)}
		>
			<div className="icon-outer">{variant && icon[variant]}</div>
			<div className="information-outer">
				<p
					className={classNames('fs-large fw-bold', {
						'mb-0': !description && !action,
						'mb-2': description || action,
					})}
				>
					{title}
				</p>
				{description && (
					<div
						className={classNames({
							'mb-2': !!action === true,
							'mb-0': !!action === false,
						})}
					>
						{description}
					</div>
				)}
				{actionsToRender.length > 0 && (
					<div>
						{actionsToRender.map((a, actionIndex) => {
							const isLast = actionIndex === actionsToRender.length - 1;

							return (
								<React.Fragment key={actionIndex}>
									<Button variant="link" size="sm" className="p-0 fw-normal" onClick={a.onClick}>
										{a.title}
									</Button>
									{!isLast && <span className="mx-1">&bull;</span>}
								</React.Fragment>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default InlineAlert;
